import React, { createContext, useContext, useId, useState } from "react";
import { LayoutGroup } from "framer-motion";
import { SegmentThumb } from "./Segmented";

// Create Tabs context
const TabsContext = createContext(null);
const getValueId = (value) => encodeURIComponent(String(value));

/**
 * Tabs Component
 *
 * A component for organizing content into multiple sections,
 * allowing users to switch between different views.
 */
export const Tabs = ({
  children,
  value,
  onValueChange,
  defaultValue,
  className = "",
  ...props
}) => {
  const isControlled = value !== undefined;
  const [selectedTab, setSelectedTab] = useState(defaultValue ?? "");
  const activeValue = isControlled ? value : selectedTab;
  const instanceId = useId();

  const handleTabChange = (newValue) => {
    if (!isControlled) {
      setSelectedTab(newValue);
    }
    onValueChange?.(newValue);
  };

  const getTriggerId = (tabValue) =>
    `${instanceId}-tab-${getValueId(tabValue)}`;
  const getPanelId = (tabValue) =>
    `${instanceId}-tabpanel-${getValueId(tabValue)}`;

  return (
    <TabsContext.Provider
      value={{
        value: activeValue,
        onChange: handleTabChange,
        getTriggerId,
        getPanelId,
      }}
    >
      {/* One layout group per Tabs, so the sliding thumb never jumps to
          another tab set on the page. */}
      <LayoutGroup id={instanceId}>
        <div className={className} {...props}>
          {children}
        </div>
      </LayoutGroup>
    </TabsContext.Provider>
  );
};

/**
 * TabsList Component
 *
 * Container for tab triggers/buttons
 */
export const TabsList = ({
  children,
  className = "",
  orientation = "horizontal",
  variant,
  ...props
}) => {
  return (
    <div
      {...props}
      className={`sg-seg ${className}`}
      data-variant={variant}
      role="tablist"
      aria-orientation={orientation}
    >
      {children}
    </div>
  );
};

/**
 * TabsTrigger Component
 *
 * The clickable tab buttons
 */
export const TabsTrigger = ({
  children,
  value,
  className = "",
  disabled = false,
  onClick,
  onKeyDown,
  ...props
}) => {
  const context = useContext(TabsContext);

  if (!context) {
    throw new Error("TabsTrigger must be used within a Tabs component");
  }

  const isActive = context.value === value;

  const handleClick = (event) => {
    onClick?.(event);
    if (!event.defaultPrevented) {
      context.onChange(value);
    }
  };

  const handleKeyDown = (event) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;

    const tabList = event.currentTarget.closest('[role="tablist"]');
    const enabledTabs = Array.from(
      tabList?.querySelectorAll('[role="tab"]:not(:disabled)') ?? [],
    );
    const currentIndex = enabledTabs.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    const orientation =
      tabList?.getAttribute("aria-orientation") ?? "horizontal";
    const previousKey =
      orientation === "vertical" ? "ArrowUp" : "ArrowLeft";
    const nextKey =
      orientation === "vertical" ? "ArrowDown" : "ArrowRight";

    let nextIndex;
    if (event.key === nextKey) {
      nextIndex = (currentIndex + 1) % enabledTabs.length;
    } else if (event.key === previousKey) {
      nextIndex =
        (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = enabledTabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    enabledTabs[nextIndex].focus();
    enabledTabs[nextIndex].click();
  };

  return (
    <button
      {...props}
      id={context.getTriggerId(value)}
      role="tab"
      type="button"
      aria-selected={isActive}
      aria-controls={context.getPanelId(value)}
      tabIndex={isActive ? 0 : -1}
      disabled={disabled}
      className={`sg-seg-item ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {isActive && <SegmentThumb />}
      {children}
    </button>
  );
};

/**
 * TabsContent Component
 *
 * The content container for a specific tab
 * @param {boolean} keepMounted - hide (not unmount) the panel while inactive
 */
export const TabsContent = ({ children, value, className = "", keepMounted = false, ...props }) => {
  const context = useContext(TabsContext);

  if (!context) {
    throw new Error("TabsContent must be used within a Tabs component");
  }

  const isActive = context.value === value;

  // keepMounted hides an inactive panel instead of unmounting it, so a panel
  // that loads its own data keeps it across tab switches.
  if (!isActive && !keepMounted) return null;

  return (
    <div
      {...props}
      hidden={!isActive || undefined}
      id={context.getPanelId(value)}
      role="tabpanel"
      aria-labelledby={context.getTriggerId(value)}
      tabIndex={0}
      className={`sg-fade mt-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--sg-focus)] ${className}`}
    >
      {children}
    </div>
  );
};

export default Tabs;
